import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ClaimModalOpenPayload, Events } from 'make-traffic-integration-core';

import { BaseModal, selectTaskManagerStatus, useMakeTrafficManager } from '@/shared';

/**
 * Hosts external content provided by the `make-traffic-integration-core` library via the `provideHost` callback.
 *
 * - Subscribes to `ClaimModalOpen` on the makeTrafficManager instance.
 * - When fired, opens a modal and exposes a container element via `provideHost`.
 * - Plugin renders its UI (e.g., SurveyJS quiz) into that container.
 * - When the user closes the modal, `ClaimModalClosed` is emitted back to the core.
 */
export const TaskClaimModalHost: React.FC = () => {
  const [state, setState] = useState<ClaimModalOpenPayload | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const provideHostCalledRef = useRef(false);

  const { makeTrafficManager } = useMakeTrafficManager();
  const taskManagerStatus = useSelector(selectTaskManagerStatus);

  // Subscribe to ClaimModalOpen when the manager is ready
  useEffect(() => {
    if (!makeTrafficManager) {
      return;
    }

    const handler = (payload: ClaimModalOpenPayload) => {
      provideHostCalledRef.current = false;

      // Ignore new open events if modal is already open
      setState((prev) => (prev ? prev : payload));
    };

    makeTrafficManager.subscribe(Events.ClaimModalOpen, handler);

    return () => {
      makeTrafficManager.unsubscribe(Events.ClaimModalOpen, handler);
    };
    // Re-subscribe if manager instance is recreated / status changes
  }, [makeTrafficManager, taskManagerStatus]);

  // Call provideHost when both container and state are available
  useEffect(() => {
    if (container && state && !provideHostCalledRef.current) {
      provideHostCalledRef.current = true;
      state.provideHost({
        container,
        close: () => setState(null)
      });
    }
  }, [container, state]);

  const isOpen = !!state;

  return (
    <BaseModal
      isOpen={isOpen}
      closeTimeoutMS={300}
      onRequestClose={() => {
        if (!state) return;
        setState(null);
        makeTrafficManager?.emit(Events.ClaimModalClosed, {
          task: state.task,
          reason: 'user_closed'
        });
      }}
      style={{
        overlay: {
          zIndex: 998 // above task modal, below header
        },
        content: {
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          top: 'auto',
          maxWidth: '640px',
          margin: '0 auto',

          height: '90vh', // big, scrollable sheet
          overflowY: 'auto',

          background: '#141528',
          padding: '0 0 20px 0',
          boxShadow: 'inset 0 0 0 1px #41424E',

          border: 'none',
          borderRadius: '10px 10px 0 0',

          transform: 'none'
        }
      }}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick>
      <div style={{ padding: 16 }}>
        <div ref={setContainer} />
      </div>
    </BaseModal>
  );
};
